<script setup lang="ts">
const intentions = [
  'BOOK A DEMO',
  'visit a design lab',
  'share a supplier inquiry',
  'request a quote',
  'learn about a role',
  'explore an investment',
  'ask a general question',
  'other',
]

const roles = [
  'brand',
  'investor',
  'talent',
  'factory',
  'press',
  // 'supply chain',
  'general',
  'other',
]

const emit = defineEmits(['success'])
const showError = ref<boolean>(false)

const { getRecaptchaToken } = useGoogleRecaptcha()

const mail = useMail()

const loading = ref<boolean>(false)

async function handleSubmit(e: Event) {
  e.preventDefault()

  if (!e.target) return

  const data = new FormData(e.target as HTMLFormElement)
  const json = Object.fromEntries(data.entries())

  // alert(JSON.stringify(json, null, 2))

  try {
    loading.value = true

    // Send the form to the API
    const token = await getRecaptchaToken('submit')

    // console.log('token', token)

    if (!token) return

    // Format the email content
    const emailBody = `
      New Contact Request

      Name: ${json['full-name']}
      Email: ${json['email']}
      Phone: ${json['phone']}
      Company: ${json['company']}
      Country: ${json['country']}

      What is your intention?
      ${json['intention']}

      As: ${json['role']}

      Message:
      ${json['message']}
  `

    const file = (data.get('file') as File) ?? null

    const toBase64 = (file: File): Promise<string | null> =>
      new Promise((resolve, reject) => {
        if (!file) return resolve(null)

        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result?.toString().split(',')[1] || '')
        reader.onerror = (error) => reject(error)
      })

    const fileBase64 = file ? await toBase64(file) : null

    const isImage = file?.type.startsWith('image/')

    const filePreview = file
      ? isImage
        ? `<div style="margin-top: 20px;">
     <p style="font-weight: bold;">📎 Attached Image Preview:</p>
     <img src="data:${file.type};base64,${fileBase64}" alt="${file.name}" style="max-width: 100%; border: 1px solid #ccc; padding: 4px; border-radius: 4px;" />
   </div>`
        : `<div style="margin-top: 20px;">
     <p style="font-weight: bold;">📎 File Attached:</p>
     <p>${file.name}</p>
   </div>`
      : null

    const htmlBody = `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; background-color: #f9f9f9;">
<h2 style="color: #333;">📬 New Contact Request</h2>

<table style="width: 100%; border-collapse: collapse;">
  <tr><td style="padding: 8px; font-weight: bold;">Name:</td><td style="padding: 8px;">${json['full-name']}</td></tr>
  <tr><td style="padding: 8px; font-weight: bold;">Email:</td><td style="padding: 8px;">${json['email']}</td></tr>
  <tr><td style="padding: 8px; font-weight: bold;">Phone:</td><td style="padding: 8px;">${json['phone']}</td></tr>
  <tr><td style="padding: 8px; font-weight: bold;">Company:</td><td style="padding: 8px;">${json['company']}</td></tr>
  <tr><td style="padding: 8px; font-weight: bold;">Country:</td><td style="padding: 8px;">${json['country']}</td></tr>
  <tr><td style="padding: 8px; font-weight: bold;">What is your intention?</td><td style="padding: 8px;">${json['intention']}</td></tr>
  <tr><td style="padding: 8px; font-weight: bold;">As:</td><td style="padding: 8px;">${json['role']}</td></tr>
</table>

<div style="margin-top: 20px;">
  <p style="font-weight: bold;">Message:</p>
  <p style="white-space: pre-wrap; background: #fff; padding: 10px; border: 1px solid #ccc; border-radius: 4px;">${json['message']}</p>
</div>

${file ? filePreview : ''}
</div>
`
    // Send email via API
    mail.send({
      from: 'nitex@nitex.com',
      replyTo: json['email'] as string,
      subject: `New Inquiry from ${json['full-name'] || json['commpany']}`,
      text: emailBody,
      html: htmlBody,
      attachments: file
        ? [
            {
              filename: file.name,
              content: fileBase64 as string,
              encoding: 'base64',
            },
          ]
        : undefined,
    })
    emit('success')
  } catch (error) {
    console.error('error:', error)
    showError.value = true
  }
}
</script>

<template>
  <form class="lg:gap-gap px-bleed lg:grid lg:grid-cols-2" @submit="handleSubmit">
    <div>
      <div class="mb-16">
        <p class="font-display mb-6 text-xl uppercase font-stretch-expanded">
          WHAT WOULD YOU LIKE TO DO
        </p>
        <div class="input-group flex flex-wrap gap-x-2 gap-y-3">
          <CommonInputRadio
            v-for="(intention, idx) in intentions"
            :key="idx"
            :label="intention"
            :name="'intention'"
          />
        </div>
      </div>

      <div class="mb-16">
        <p class="font-display mb-6 text-xl uppercase font-stretch-expanded">
          I'm contacting you as
        </p>
        <div class="input-group flex flex-wrap gap-x-2 gap-y-3">
          <CommonInputRadio v-for="(role, idx) in roles" :key="idx" :label="role" :name="'role'" />
        </div>
      </div>
    </div>

    <FormsBase :show-form-error="showError" :loading="loading" />
  </form>
</template>
